<template>
  <div class="fin-screen">
    <div class="fin-toolbar">
      <h2 class="fin-title">{{ $t("finance_tab_settlements") }}</h2>
      <span class="fin-sub">{{ $t("finance_dynamics_label").toLowerCase() }}: {{ scheduleLabel }}</span>
      <span class="fin-grow"></span>
      <finance-export :filename="`obracuni_${shell.restaurantId}`" :get-data="getExportData" :disabled="!rows.length" @print="shell.printPage" />
      <button v-if="shell.isAdmin" type="button" class="ui-btn ui-btn--primary" @click="openRecord">
        <v-icon size="16" color="currentColor">mdi-plus</v-icon>{{ $t("finance_record_payment_generic") }}
      </button>
    </div>

    <finance-state v-if="status === 'loading'" kind="loading" :lines="3" />
    <finance-state v-else-if="status === 'error'" kind="error" icon="mdi-alert-circle-outline" :title="$t('finance_shell.error_title')" :text="$t('finance_settlements_load_error')">
      <button type="button" class="ui-btn ui-btn--dark" @click="load"><v-icon size="16">mdi-refresh</v-icon>{{ $t("finance_shell.retry") }}</button>
    </finance-state>

    <template v-else>
      <section class="ui-card fin-stats">
        <div>
          <small>{{ $t("finance_current_balance_label") }}</small>
          <b v-if="balance !== null" class="fin-num" :class="balance >= 0 ? 'is-in' : 'is-out'">{{ balance >= 0 ? $t("finance_hero_you_receive_short") : $t("finance_hero_you_owe") }} {{ money(Math.abs(balance)) }}</b>
          <b v-else>—</b>
        </div>
        <div>
          <small>{{ $t("finance_next_settlement") }}</small>
          <b>{{ settlement.nextDate ? localeDate(settlement.nextDate) : "—" }}</b>
          <small v-if="settlement.schedule">{{ scheduleLabel }}</small>
        </div>
        <div>
          <small>{{ $t("finance_open_from_previous") }}</small>
          <b class="fin-num">{{ money(open) }}</b>
        </div>
      </section>

      <div v-if="conflict" class="ui-banner ui-banner--info ui-banner--warn">
        <v-icon size="16" color="currentColor">mdi-alert-outline</v-icon>
        <span>{{ $t("finance_schedule_conflict") }} <button type="button" class="ui-link" @click="shell.openScreen('contract')">{{ $t("finance_shell.open_contract") }}</button></span>
      </div>

      <finance-state v-if="!rows.length" kind="empty" icon="mdi-file-document-outline" :title="$t('finance_no_settlements')" />

      <section v-else class="ui-card fin-table-card">
        <div class="ui-table-wrap">
          <table class="ui-table">
            <thead>
              <tr>
                <th>{{ $t("finance_period_label") }}</th>
                <th class="r">{{ $t("finance_turnover") }}</th>
                <th class="r">{{ $t("finance_deductions") }}</th>
                <th class="r">{{ $t("finance_net") }}</th>
                <th>{{ $t("finance_status_label") }}</th>
                <th>{{ $t("finance_document") }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in rows" :key="row.key" class="is-click" :class="{ 'is-estimate': row.kind === 'estimate', 'is-on': activeKey === row.key }" tabindex="0" @click="openRow(row)" @keyup.enter="openRow(row)">
                <td>
                  <b>{{ periodText(row) }}</b>
                  <small v-if="row.kind === 'estimate'">{{ $t("finance_shell.settle.estimate_caption") }}</small>
                </td>
                <td class="r fin-num">{{ cell(row.turnover) }}</td>
                <td class="r fin-num">{{ cell(row.deductions) }}</td>
                <td class="r fin-num"><b>{{ money(row.net) }}</b></td>
                <td><span class="ui-chip ui-chip--pill" :class="chipClass(row)">{{ statusText(row) }}</span></td>
                <td>
                  <button v-if="row.kind !== 'estimate'" type="button" class="ui-link" @click.stop="printRow(row)">PDF</button>
                  <span v-else>—</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="fin-cardlist">
          <button v-for="row in rows" :key="`c-${row.key}`" type="button" class="fin-tx" :class="`is-${tone(row)}`" @click="openRow(row)">
            <span class="fin-tx__head"><b>{{ periodText(row) }}</b><span class="fin-tx__net fin-num">{{ money(row.net) }}</span></span>
            <span class="fin-tx__lines">
              <span>{{ $t("finance_turnover") }} <b class="fin-num">{{ cell(row.turnover) }}</b></span>
              <span>{{ $t("finance_deductions") }} <b class="fin-num">{{ cell(row.deductions) }}</b></span>
            </span>
            <span><span class="ui-chip ui-chip--pill" :class="chipClass(row)">{{ statusText(row) }}</span></span>
          </button>
        </div>
      </section>
    </template>
  </div>
</template>

<script>
import restaurantFinanceService from "@/services/restaurantFinance";
import restaurantService from "@/services/restaurant";
import { PAYOUT_SCHEDULE_LABEL_KEYS, PAYOUT_STATUS_META, formatMoney, resolveSettlement } from "@/components/restaurant/finance/constants";
import { parseOverview } from "@/components/restaurant/finance/overview";
import { formatRange, formatShortDate } from "@/components/report/shared/periods";
import FinanceExport from "../parts/FinanceExport.vue";
import FinanceState from "../parts/FinanceState.vue";
import RecordPanel from "../panels/RecordPanel.vue";
import SettlePanel from "../panels/SettlePanel.vue";
import hasFinancePrint from "../hasFinancePrint";
import { buildEstimate, estimateRange, mapPayment, mapPayout, openFromPrevious, settlementRows, settlementStatus } from "../financeLogic";

/**
 * Obračuni: stanje, sljedeći obračun, otvoreno iz ranijih obračuna i lista (procjena tekućeg perioda, isplate i uplate
 * restorana). Red otvara detalj u panelu; administrator evidentira isplatu/uplatu iz panela na zahtjev.
 * Dok nema Settlement faze, "obračun" je isplata iz GET /payouts, a procjena se računa iz financial-overview.
 */
export default {
  name: "FinanceSettlements",
  components: { FinanceExport, FinanceState },
  mixins: [hasFinancePrint],
  inject: { shell: "reportShell" },
  props: { period: { type: Object, default: null } },
  data: () => ({ status: "loading", rows: [], range: null, raw: null, services: [], activeKey: "" }),
  computed: {
    printTitle() {
      return this.$t("finance_tab_settlements");
    },
    currency() {
      return (this.raw && this.raw.currency) || this.shell.currency;
    },
    balance() {
      return this.raw && this.raw.current_balance !== null && this.raw.current_balance !== undefined ? Number(this.raw.current_balance) : null;
    },
    contract() {
      const withContract = this.services.find((service) => service.is_active && service.contract && service.contract.payout_schedule);
      return withContract ? withContract.contract : null;
    },
    settlement() {
      return resolveSettlement(this.raw && this.raw.settlement, this.contract);
    },
    conflict() {
      return Boolean(this.shell.isAdmin && this.raw && this.raw.settlement && this.raw.settlement.conflict);
    },
    scheduleLabel() {
      const { schedule, thresholdAmount } = this.settlement;
      if (!schedule) {
        return this.$t("finance_schedule_not_agreed");
      }
      if (schedule === "THRESHOLD" && thresholdAmount !== null && thresholdAmount !== undefined) {
        return this.$t("finance_schedule_threshold_amount", { amount: this.money(thresholdAmount) });
      }
      return this.$t(PAYOUT_SCHEDULE_LABEL_KEYS[schedule] || "finance_schedule_not_agreed");
    },
    open() {
      return openFromPrevious(this.rows);
    },
  },
  created() {
    this.load();
  },
  methods: {
    money(value) {
      return formatMoney(value, this.currency);
    },
    cell(value) {
      return value === null || value === undefined ? "—" : this.money(value);
    },
    /** boja oznake statusa: plaćeno zeleno, procjena neutralno, ostalo upozorenje */
    chipClass(row) {
      const tone = this.tone(row);
      return tone === "done" ? "ui-chip--ok" : tone === "est" ? "" : "ui-chip--warn";
    },
    tone(row) {
      const status = settlementStatus(row);
      return status === "paid" || status === "received" ? "done" : status === "estimate" ? "est" : "wait";
    },
    shortDate(value) {
      return value ? formatShortDate(String(value).slice(0, 10)) : "";
    },
    statusText(row) {
      const status = settlementStatus(row);
      if (status === "estimate") return this.$t("finance_shell.settle.estimate");
      if (status === "received") return `${this.$t("finance_status_payment_received")} ${this.shortDate(row.date)}`;
      if (status === "paid") return `${this.$t(PAYOUT_STATUS_META.paid.labelKey)} ${this.shortDate(row.date)}`;
      if (status === "partial") return this.$t("finance_shell.settle.partial", { amount: this.money(row.remaining) });
      const meta = PAYOUT_STATUS_META[row.status];
      return row.status === "pending" ? this.$t("finance_status_confirmed_waiting") : meta ? this.$t(meta.labelKey) : row.status || "—";
    },
    periodText(row) {
      if (row.kind === "estimate" || (row.from && row.to && !row.periodLabel)) {
        return formatRange(row.from, row.to);
      }
      return row.periodLabel || (row.from && row.to ? formatRange(row.from, row.to) : "—");
    },
    async load() {
      const id = this.shell.restaurantId;
      if (!id) {
        return;
      }
      this.status = "loading";
      try {
        const [payoutsRes, paymentsRes, overviewRes, servicesRes] = await Promise.all([
          restaurantFinanceService.getPayouts(id),
          restaurantFinanceService.getPlatformPayments(id),
          // current_balance i settlement ne zavise od perioda, pa idu bez ?from&to
          restaurantFinanceService.getFinancialOverview(id).catch(() => null),
          restaurantService.getRestaurantServices(id).catch(() => null),
        ]);
        const payouts = ((payoutsRes.data && payoutsRes.data.data) || []).map(mapPayout);
        const payments = (((paymentsRes.data && paymentsRes.data.data) || {}).payments || []).map(mapPayment);
        this.raw = (overviewRes && ((overviewRes.data && overviewRes.data.data) || overviewRes.data)) || {};
        const data = servicesRes && servicesRes.data;
        this.services = (data && (data.services || (data.data && data.data.services))) || [];

        this.range = estimateRange(payouts);
        let estimate = null;
        if (this.range) {
          try {
            const res = await restaurantFinanceService.getFinancialOverview(id, this.range);
            estimate = buildEstimate(parseOverview((res.data && res.data.data) || res.data || {}, this.currency), this.range);
          } catch (error) {
            estimate = null;
          }
        }
        this.rows = settlementRows(payouts, payments, estimate);
        this.status = "ready";
      } catch (error) {
        this.status = "error";
      }
    },
    openRow(row) {
      this.activeKey = row.key;
      this.shell.openPanel({
        key: `settle-${row.key}`,
        title: this.$t("finance_shell.settle.title", { period: this.periodText(row) }),
        subtitle: this.statusText(row),
        component: SettlePanel,
        props: { row, restaurantId: this.shell.restaurantId, currency: this.currency },
        root: true,
      });
    },
    printRow(row) {
      this.openRow(row);
      this.$nextTick(() => setTimeout(this.shell.printPanel, 450));
    },
    openRecord() {
      this.shell.openPanel({
        key: "record-payment",
        title: this.$t("finance_record_payment_generic"),
        subtitle: this.$t("finance_admin_only"),
        component: RecordPanel,
        props: { restaurantId: this.shell.restaurantId, currency: this.currency, balance: this.balance, range: this.range },
        root: true,
      });
    },
    getExportData() {
      return {
        headers: [this.$t("finance_period_label"), this.$t("finance_turnover"), this.$t("finance_deductions"), `${this.$t("finance_net")} (${this.currency})`, this.$t("finance_status_label")],
        rows: this.rows.map((row) => [this.periodText(row), row.turnover === null ? "" : row.turnover, row.deductions === null ? "" : row.deductions, row.net, this.statusText(row)]),
        numeric: [1, 2, 3],
      };
    },
  },
};
</script>
